// Offline outbox (Directive 1142/2026 business continuity).
//
// When the device has no connection, write actions (check-in / check-out for
// now) are stored here in IndexedDB and replayed automatically when the
// connection returns. Each item carries a stable idempotency_key so a replay
// can never double-apply on the server. This module is transport-agnostic: it
// only stores and hands back items — api.js owns the actual sending.

const DB_NAME = "odra-offline";
const STORE = "outbox";
const DB_VERSION = 1;

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("no-indexeddb")); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function tx(mode) {
  return openDB().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

// --- change notification so the banner can show a live pending count ---
const _listeners = new Set();
export function onQueueChange(cb) { _listeners.add(cb); return () => _listeners.delete(cb); }
function _emit() { _listeners.forEach((cb) => { try { cb(); } catch (e) { /* noop */ } }); }

// item: { kind, label, method, path, body }  (path = API path, e.g. "/reservations/5/check-in")
export async function enqueue(item) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const rec = { ...item, createdAt: Date.now() };
    const req = store.add(rec);
    req.onsuccess = () => { _emit(); resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

export async function allItems() {
  const store = await tx("readonly");
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function removeItem(id) {
  const store = await tx("readwrite");
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => { _emit(); resolve(); };
    req.onerror = () => reject(req.error);
  });
}

export async function count() {
  try {
    const store = await tx("readonly");
    return await new Promise((resolve, reject) => {
      const req = store.count();
      req.onsuccess = () => resolve(req.result || 0);
      req.onerror = () => reject(req.error);
    });
  } catch (e) { return 0; }
}

// A short client-generated key that ties a queued action to its server record,
// so replaying it is a safe no-op.
export function newKey() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) { /* fall through */ }
  return "k-" + Date.now() + "-" + Math.random().toString(16).slice(2, 10);
}
